/**
 * CommissionLedger - Platform Fees (R1,500 per Litter)
 * ThePedigreeApp
 */

import { useState, useEffect } from 'react';
import { DollarSign, Calendar, FileText, Download, Check, Clock, AlertCircle, CreditCard, Baby, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, Button, Badge, Alert } from '../shared/UIComponents';
import { cn } from '../../utils/helpers';

const LITTER_FEE = 1500;

const CommissionLedger = () => {
  const [entries, setEntries] = useState([
    { id: '1', type: 'litter_fee', litterName: 'Luna x Prince - Jan 2024', litterRef: 'LIT-2024-001', kittensCount: 5, kittensSold: 4, feeAmount: LITTER_FEE, status: 'paid', dueDate: '2024-02-15', paidDate: '2024-02-10', invoiceNumber: 'INV-2024-0001' },
    { id: '2', type: 'litter_fee', litterName: 'Bella x Shadow - Feb 2024', litterRef: 'LIT-2024-002', kittensCount: 3, kittensSold: 3, feeAmount: LITTER_FEE, status: 'pending', dueDate: '2024-03-20', paidDate: null, invoiceNumber: 'INV-2024-0002' },
    { id: '3', type: 'litter_fee', litterName: 'Misty x King - Mar 2024', litterRef: 'LIT-2024-003', kittensCount: 6, kittensSold: 2, feeAmount: LITTER_FEE, status: 'overdue', dueDate: '2024-04-01', paidDate: null, invoiceNumber: 'INV-2024-0003' },
  ]);
  const [expanded, setExpanded] = useState(null);

  const totalFees = entries.reduce((s, e) => s + e.feeAmount, 0);
  const totalPaid = entries.filter(e => e.status === 'paid').reduce((s, e) => s + e.feeAmount, 0);
  const totalPending = entries.filter(e => e.status === 'pending').reduce((s, e) => s + e.feeAmount, 0);
  const totalOverdue = entries.filter(e => e.status === 'overdue').reduce((s, e) => s + e.feeAmount, 0);

  const getStatus = (s) => {
    if (s === 'paid') return { label: 'Paid', icon: Check, bg: 'bg-green-100', text: 'text-green-700' };
    if (s === 'pending') return { label: 'Pending', icon: Clock, bg: 'bg-yellow-100', text: 'text-yellow-700' };
    return { label: 'Overdue', icon: AlertCircle, bg: 'bg-red-100', text: 'text-red-700' };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-display font-bold text-gray-900">Commission Ledger</h1><p className="text-gray-500">Platform fees - R{LITTER_FEE.toLocaleString()} per litter sold</p></div>
        <div className="flex gap-2"><Button variant="outline"><Download className="w-4 h-4" /> Export</Button><Button variant="outline"><FileText className="w-4 h-4" /> Invoices</Button></div>
      </div>

      <Alert type="info">
        <DollarSign className="w-5 h-5 text-blue-600" />
        <div><p className="font-medium text-blue-800">Platform Fee Structure</p><p className="text-sm text-blue-700 mt-1"><strong>R{LITTER_FEE.toLocaleString()}</strong> flat fee per litter sold. Fee applies once any kitten from the litter is sold. Payment due within 14 days.</p></div>
      </Alert>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-pedigree-100 rounded-xl flex items-center justify-center"><Baby className="w-5 h-5 text-pedigree-600" /></div><div><p className="text-sm text-gray-500">Litters</p><p className="text-xl font-bold">{entries.length}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"><DollarSign className="w-5 h-5 text-gray-600" /></div><div><p className="text-sm text-gray-500">Total Fees</p><p className="text-xl font-bold">R{totalFees.toLocaleString()}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><Check className="w-5 h-5 text-green-600" /></div><div><p className="text-sm text-gray-500">Paid</p><p className="text-xl font-bold text-green-600">R{totalPaid.toLocaleString()}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-600" /></div><div><p className="text-sm text-gray-500">Pending</p><p className="text-xl font-bold text-yellow-600">R{totalPending.toLocaleString()}</p></div></div></Card>
        <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center"><AlertCircle className="w-5 h-5 text-red-600" /></div><div><p className="text-sm text-gray-500">Overdue</p><p className="text-xl font-bold text-red-600">R{totalOverdue.toLocaleString()}</p></div></div></Card>
      </div>

      {(totalPending + totalOverdue) > 0 && (
        <Card className="p-4 bg-gradient-to-r from-pedigree-500 to-pedigree-600 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div><p className="text-pedigree-100 text-sm">Outstanding Balance</p><p className="text-3xl font-bold">R{(totalPending + totalOverdue).toLocaleString()}</p></div>
            <Button className="bg-white text-pedigree-600 hover:bg-pedigree-50"><CreditCard className="w-4 h-4" /> Pay Now</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 border-b bg-gray-50"><h2 className="font-semibold">Fee History</h2></div>
        <div className="divide-y">
          {entries.map((e) => {
            const st = getStatus(e.status);
            const Icon = st.icon;
            const isExp = expanded === e.id;
            return (
              <div key={e.id} className="hover:bg-gray-50">
                <div className="p-4 cursor-pointer" onClick={() => setExpanded(isExp ? null : e.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', st.bg)}><Icon className={cn('w-5 h-5', st.text)} /></div>
                      <div><p className="font-medium">{e.litterName}</p><p className="text-sm text-gray-500">{e.kittensCount} kittens • {e.invoiceNumber}</p></div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right"><p className="font-bold">R{e.feeAmount.toLocaleString()}</p><p className="text-sm text-gray-500">Due: {new Date(e.dueDate).toLocaleDateString('en-ZA')}</p></div>
                      <Badge className={cn(st.bg, st.text)}>{st.label}</Badge>
                      {isExp ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                </div>
                {isExp && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <div className="ml-14 flex gap-2">
                      <Button variant="outline" size="sm"><FileText className="w-4 h-4" /> View Invoice</Button>
                      {e.status !== 'paid' && <Button size="sm"><CreditCard className="w-4 h-4" /> Pay R{e.feeAmount.toLocaleString()}</Button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold mb-4">Payment Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><p className="text-sm text-gray-500 mb-2">Bank Details</p><div className="bg-gray-50 rounded-xl p-4 text-sm"><p><strong>Bank:</strong> FNB</p><p><strong>Account:</strong> ThePedigreeApp (Pty) Ltd</p><p><strong>Number:</strong> 62123456789</p><p><strong>Branch:</strong> 250655</p><p><strong>Reference:</strong> Your Invoice Number</p></div></div>
          <div><p className="text-sm text-gray-500 mb-2">Payment Terms</p><ul className="text-sm text-gray-600 space-y-2"><li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5" />R{LITTER_FEE.toLocaleString()} flat fee per litter (any size)</li><li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5" />Fee applies when first kitten sold</li><li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-0.5" />Payment due within 14 days</li><li className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />Late payments may suspend account</li></ul></div>
        </div>
      </Card>
    </div>
  );
};

export default CommissionLedger;
